import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import CloseIcon from "@mui/icons-material/Close";

import hostCapture, { CAPTURE_STATE } from "../liveTap/hostCapture/hostCapture";
import useHostCapture from "../hooks/useHostCapture";
import { formatElapsed } from "../services/utilities";
import styles from "./HostCaptureBar.module.scss";

// The capture bar (TT-1102): shown on the Live Tap page while THIS tab is the
// capture agent for the doc — source, level, elapsed, mute, stop. Stopping
// ends the tapestry (close drip), so it asks once.

// MUI's default icon colour is near-black; the page themes its icon buttons
// through the CSS variables (same as LiveTapPage's transport row).
const ICON_BTN_SX = {
  color: "var(--color-text-secondary)",
  "&:hover": { color: "var(--color-text-primary)" },
  "&.Mui-disabled": { color: "var(--color-text-muted)" },
};
const STOP_BTN_SX = {
  color: "var(--color-error)",
  "&.Mui-disabled": { color: "var(--color-text-muted)" },
};

function stateLabel(state) {
  switch (state) {
    case CAPTURE_STATE.STARTING:
      return "Starting";
    case CAPTURE_STATE.RECONNECTING:
      return "Reconnecting";
    case CAPTURE_STATE.STOPPING:
      return "Stopping";
    case CAPTURE_STATE.STOPPED:
      return "Ended";
    case CAPTURE_STATE.ERROR:
      return "Failed";
    default:
      return "Live";
  }
}

const HostCaptureBar = ({ docId }) => {
  const capture = useHostCapture();
  const [elapsed, setElapsed] = useState(hostCapture.elapsedSeconds());
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!capture.active) return undefined;
    const tick = setInterval(() => setElapsed(hostCapture.elapsedSeconds()), 1000);
    return () => clearInterval(tick);
  }, [capture.active]);

  const stop = useCallback(async () => {
    setConfirmOpen(false);
    await hostCapture.stop();
  }, []);

  if (capture.docId !== docId || capture.dismissed) return null;
  const ended = capture.state === CAPTURE_STATE.STOPPED || capture.state === CAPTURE_STATE.ERROR;
  if (capture.state === CAPTURE_STATE.IDLE) return null;

  const running = capture.state === CAPTURE_STATE.RUNNING;
  const levelPct = Math.min(100, Math.round(capture.level * 400));

  return (
    <div className={styles.bar} role="status" aria-live="polite">
      <span className={classNames(styles.rec, { [styles.quiet]: !running || capture.muted })}>
        {stateLabel(capture.state)}
      </span>
      <span className={styles.source} title={capture.sourceLabel}>
        {capture.sourceLabel}
      </span>
      {!ended && (
        <div className={styles.meter} aria-hidden>
          <span style={{ width: `${capture.muted ? 0 : levelPct}%` }} />
        </div>
      )}
      <span className={styles.elapsed}>{formatElapsed(elapsed)}</span>
      {capture.lastText && !ended && <span className={styles.last}>{capture.lastText}</span>}
      <div className={styles.actions}>
        {!ended && (
          <Tooltip title={capture.muted ? "Unmute" : "Mute"}>
            <IconButton size="small" sx={ICON_BTN_SX} onClick={() => hostCapture.setMuted(!capture.muted)}>
              {capture.muted ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {!ended && (
          <Tooltip title="Stop and end this tapestry">
            <span>
              <IconButton
                size="small"
                sx={STOP_BTN_SX}
                onClick={() => setConfirmOpen(true)}
                disabled={capture.state === CAPTURE_STATE.STOPPING}>
                <StopCircleIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {ended && (
          <IconButton size="small" sx={ICON_BTN_SX} onClick={() => hostCapture.dismiss()} aria-label="Dismiss">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      {capture.error && <span className={styles.error}>{capture.error}</span>}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Stop capturing?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This ends the live tapestry. The transcript and tapestry so far are kept; capture cannot be resumed into the
            same tapestry.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Keep going</Button>
          <Button color="error" onClick={stop}>
            Stop
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

HostCaptureBar.propTypes = {
  docId: PropTypes.string,
};

export default HostCaptureBar;
